import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { InfoCard, Link } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { FlagSummary, STATUS_COLORS, useEntityGrowthBook } from '../health/api';

const ENVIRONMENTS = [
  { key: 'production', label: 'web' },
  { key: 'production_backend', label: 'backend' },
  { key: 'production_mobile', label: 'mobile' },
];

const useStyles = makeStyles(theme => ({
  cell: { padding: theme.spacing(0.5, 1) },
  key: { fontFamily: 'monospace', fontSize: 12 },
  muted: { color: theme.palette.text.secondary, fontSize: 12 },
  section: { fontWeight: 600, marginBottom: theme.spacing(0.5) },
  changed: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'baseline',
    fontSize: 13,
    padding: theme.spacing(0.25, 0),
  },
  block: { marginBottom: theme.spacing(2) },
}));

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const EnvState = ({ flag, env }: { flag: FlagSummary; env: string }) => {
  const classes = useStyles();
  const e = flag.environments[env];
  if (!e) return <span className={classes.muted}>—</span>;
  if (!e.enabled) return <span className={classes.muted}>выкл</span>;
  return (
    <span style={{ color: STATUS_COLORS.ok }}>
      {e.defaultValue.length > 12 ? 'вкл' : e.defaultValue}
      {e.activeRules ? (
        <span className={classes.muted}> +{e.activeRules} правил</span>
      ) : null}
    </span>
  );
};

const FlagKey = ({ flag }: { flag: FlagSummary }) => {
  const classes = useStyles();
  return flag.url ? (
    <Link to={flag.url} className={classes.key}>
      {flag.key}
    </Link>
  ) : (
    <span className={classes.key}>{flag.key}</span>
  );
};

export const GrowthBookCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { value } = useEntityGrowthBook(stringifyEntityRef(entity));
  if (!value?.configured) return null;
  const cell = classes.cell;

  return (
    <Grid item xs={12}>
      <InfoCard
        title="Фича-флаги"
        subheader={`${value.total} флагов, production-окружения`}
      >
        <div className={classes.block}>
          <Typography className={classes.section}>
            Изменены за 7 дней
          </Typography>
          {value.changed.length ? (
            value.changed.map(f => (
              <div key={f.key} className={classes.changed}>
                <FlagKey flag={f} />
                <span className={classes.muted}>
                  {formatDate(f.updatedAt)}
                  {f.version ? ` · версия ${f.version}` : ''}
                  {f.publishedBy ? ` · ${f.publishedBy}` : ''}
                </span>
              </div>
            ))
          ) : (
            <Typography className={classes.muted}>Изменений не было</Typography>
          )}
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={cell}>Флаг</TableCell>
              {ENVIRONMENTS.map(e => (
                <TableCell key={e.key} className={cell}>
                  {e.label}
                </TableCell>
              ))}
              <TableCell className={cell}>Теги</TableCell>
              <TableCell className={cell} align="right">
                Изменён
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {value.flags.map(f => (
              <TableRow key={f.key}>
                <TableCell className={cell}>
                  <FlagKey flag={f} />
                  {f.description && (
                    <div className={classes.muted}>{f.description}</div>
                  )}
                </TableCell>
                {ENVIRONMENTS.map(e => (
                  <TableCell key={e.key} className={cell}>
                    <EnvState flag={f} env={e.key} />
                  </TableCell>
                ))}
                <TableCell className={`${cell} ${classes.muted}`}>
                  {f.tags.join(', ')}
                </TableCell>
                <TableCell className={`${cell} ${classes.muted}`} align="right">
                  {formatDate(f.updatedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </InfoCard>
    </Grid>
  );
};
